/**
 * VigilanceAchats — Miroir strict de Vigilance côté fournisseurs.
 *
 * Détecte les dépenses récurrentes manquantes (loyer, abonnements SaaS,
 * honoraires expert-comptable) — factures fournisseurs présentes M-1 et
 * M-2 mais absentes ce mois.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle, EyeOff, Settings, Send, RefreshCw, History,
  CheckCircle, Building2, Euro, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

const genMonths = () => {
  const opts = [];
  const now = new Date();
  const MOIS = ["janvier","février","mars","avril","mai","juin",
                "juillet","août","septembre","octobre","novembre","décembre"];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: `${MOIS[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  return opts;
};

const PriorityBadge = ({ priority }) => {
  const cfg = {
    high: "bg-red-100 text-red-700 border-red-200",
    medium: "bg-amber-100 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const label = { high: "Haute", medium: "Moyenne", low: "Basse" };
  return <Badge className={cfg[priority] || cfg.low}>{label[priority] || "Basse"}</Badge>;
};

export default function VigilanceAchats() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("expenses.write");
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [sending, setSending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    sender_email: "", recipient_email: "", cc_email: "",
    notification_day: 5, enabled: false,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [history, setHistory] = useState([]);
  const monthOpts = useMemo(genMonths, []);

  const loadDefault = async () => {
    try {
      const { data } = await api.get("/vigilance-achats/default-month");
      setSelectedMonth(data.month);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadDefault(); }, []);

  const load = async () => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/vigilance-achats?month=${selectedMonth}`);
      setAlerts(data.alerts || []);
      setStats(data);
      if (data.settings) setSettings((s) => ({ ...s, ...data.settings }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (selectedMonth) load(); }, [selectedMonth]);  // eslint-disable-line

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/vigilance-achats/history");
      setHistory(data.history || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const ignore = async (a) => {
    try {
      await api.post("/vigilance-achats/ignore", {
        supplier_key: a.supplier_key, amount: a.rounded_amount, month: selectedMonth,
      });
      toast.success("Alerte ignorée");
      setAlerts((prev) => prev.filter((x) => x.supplier_key !== a.supplier_key
                                              || x.rounded_amount !== a.rounded_amount));
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendNotification = async () => {
    if (!settings.recipient_email) {
      toast.error("Configurez d'abord les paramètres."); return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/vigilance-achats/send-notification",
                                        { month: selectedMonth });
      if (data.sent) toast.success(`Email envoyé (${data.alerts_count} alertes)`);
      else toast.info("Aucune alerte à notifier ce mois.");
      loadHistory();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const saveSettings = async () => {
    if (!settings.sender_email || !settings.recipient_email) {
      toast.error("Sender et recipient obligatoires."); return;
    }
    setSavingSettings(true);
    try {
      await api.post("/vigilance-achats/settings", {
        ...settings,
        cc_email: settings.cc_email || null,
        notification_day: Number(settings.notification_day) || 5,
      });
      toast.success("Paramètres enregistrés"); setSettingsOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingSettings(false); }
  };

  return (
    <div className="space-y-6" data-testid="vigilance-achats-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <AlertTriangle className="h-7 w-7 text-amber-500" />
            Vigilance Achats
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Détection intelligente des dépenses récurrentes manquantes — loyer,
            abonnements SaaS, honoraires expert-comptable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedMonth || ""} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-56" data-testid="vig-achats-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOpts.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}
                   data-testid="vig-achats-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Détecter
          </Button>
          {canWrite && (
            <Button variant="outline" onClick={() => setSettingsOpen(true)}
                     data-testid="vig-achats-settings-btn">
              <Settings className="w-4 h-4 mr-1.5" /> Paramètres
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 flex gap-3">
        <div className="text-blue-700 shrink-0">💡</div>
        <div className="text-sm text-blue-900">
          <b>Comment ça marche :</b> Averdy Agency compare les factures fournisseurs
          validées/payées des 2 mois précédents et détecte celles absentes ce
          mois. Un abonnement SaaS mensuel de <span className="font-mono">120 € HT</span>
          {" "}payé en juillet ET août mais absent en septembre déclenche une alerte.
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Alertes</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{stats.total_alerts || 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Priorité haute</div>
          <div className="mt-1 text-2xl font-semibold font-mono text-red-700">{stats.high_priority || 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Dépenses à risque</div>
          <div className="mt-1 text-2xl font-semibold font-mono text-amber-700">
            {fmtEUR(stats.total_at_risk_ht)}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Fournisseurs récurrents</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{stats.recurring_suppliers_detected || 0}</div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="alerts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            Alertes ({alerts.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="h-3.5 w-3.5 mr-1.5" />Historique envois
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alerts">
          <Card>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {stats.month_label && <span>Mois analysé : <b>{stats.month_label}</b> (vs {stats.prev_month_label}, {stats.two_months_ago_label})</span>}
              </div>
              {canWrite && alerts.length > 0 && (
                <Button onClick={sendNotification} disabled={sending}
                         size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
                         data-testid="vig-achats-send-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {sending ? "Envoi…" : "Envoyer notification"}
                </Button>
              )}
            </div>
            {alerts.length === 0 ? (
              <div className="p-16 text-center">
                <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto" />
                <p className="mt-3 text-sm text-slate-600 font-medium">
                  Aucune dépense récurrente manquante ce mois. 🎉
                </p>
              </div>
            ) : (
              <Table data-testid="vig-achats-alerts-table">
                <TableHeader><TableRow>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead className="text-right">Montant estimé HT</TableHead>
                  <TableHead>Priorité</TableHead>
                  <TableHead>Dernière facture</TableHead>
                  <TableHead>M-2</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  <AnimatePresence>
                    {alerts.map((a) => (
                      <motion.tr key={`${a.supplier_key}-${a.rounded_amount}`}
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, x: -100 }}
                                  data-testid={`vig-achats-row-${a.supplier_key}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                            <Building2 className="h-3.5 w-3.5 text-slate-400" />
                            <span className="font-medium">{a.supplier_name}</span>
                            {a.pcg_code && (
                              <span className="text-[10px] font-mono text-slate-400">PCG {a.pcg_code}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {fmtEUR(a.expected_amount_ht)}
                        </TableCell>
                        <TableCell><PriorityBadge priority={a.priority} /></TableCell>
                        <TableCell className="text-xs">
                          <span className="font-mono text-blue-700">{a.last_invoice_m1?.number || "—"}</span>
                          <span className="text-slate-500 ml-2">{fmtDate(a.last_invoice_m1?.date)}</span>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {fmtDate(a.invoice_m2?.date)}
                        </TableCell>
                        <TableCell>
                          {canWrite && (
                            <Button variant="ghost" size="sm" onClick={() => ignore(a)}
                                     data-testid={`vig-achats-ignore-${a.supplier_key}`}
                                     className="h-8">
                              <EyeOff className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Envoyé le</TableHead>
                <TableHead>Mois analysé</TableHead>
                <TableHead className="text-right">Alertes</TableHead>
                <TableHead className="text-right">Montant à risque</TableHead>
                <TableHead>Par</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Aucun envoi encore.
                  </TableCell></TableRow>
                )}
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-mono text-xs">{fmtDate(h.sent_at)}</TableCell>
                    <TableCell>{h.month_label}</TableCell>
                    <TableCell className="text-right font-mono">{h.alerts_count}</TableCell>
                    <TableCell className="text-right font-mono">{fmtEUR(h.total_at_risk_ht)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{h.sent_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Paramètres Vigilance Achats</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Expéditeur</Label>
              <Input value={settings.sender_email || ""}
                      onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                      placeholder="notifications@votre-domaine.com"
                      data-testid="vig-achats-sender" />
            </div>
            <div>
              <Label className="text-xs">Destinataire *</Label>
              <Input value={settings.recipient_email || ""}
                      onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
                      placeholder="comptabilite@…"
                      data-testid="vig-achats-recipient" />
            </div>
            <div>
              <Label className="text-xs">CC (facultatif)</Label>
              <Input value={settings.cc_email || ""}
                      onChange={(e) => setSettings({ ...settings, cc_email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Jour d'envoi (1-28)</Label>
                <Input type="number" min="1" max="28"
                        value={settings.notification_day}
                        onChange={(e) => setSettings({ ...settings, notification_day: e.target.value })}
                        className="font-mono" />
              </div>
              <div className="flex items-end justify-between border rounded-md px-3 py-2">
                <Label className="text-xs">Envoi auto</Label>
                <Switch checked={settings.enabled}
                         onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                         data-testid="vig-achats-enabled" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Annuler</Button>
            <Button onClick={saveSettings} disabled={savingSettings}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="vig-achats-save-settings">
              {savingSettings ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
